// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/shared/agent-capabilities.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
import { AGENT_SOLAR_BODIES, AGENT_SOLAR_SPEEDS, type AgentCapability, type AgentCommand } from './protocol';
import { MAX_VIEW_LATITUDE_DEG, MIN_VIEW_LATITUDE_DEG } from './flags';

/**
 * The deepest zoom an agent may ask for: AYNI 3D's MAX_ZOOM_VALUE (flags.ts),
 * the city close-ups. Kept here because AYNI XR's copy of flags.ts is older.
 */
export const AGENT_MAX_ZOOM = 8192;
/** The fastest turning an agent may ask for: the rotation speed slider's top. */
export const AGENT_MAX_ROTATION_SPEED = 10;

/**
 * Single source of truth for the agent capability advertisements.
 *
 * Consumed by both the WebSocket `AGENT_CAPABILITIES` handler (main) and the
 * renderer `AgentControlService` (renderer) so the advertised surface and the
 * implemented surface cannot drift. Flipping `enabled` here is the one place
 * that gates whether a capability is advertised as available.
 */
export const AGENT_CAPABILITIES: ReadonlyArray<AgentCapability> = [
  {
    name: 'catalog.search',
    enabled: true,
    description: 'Search dataset titles, descriptions, categories, keywords, provider, and units.'
  },
  {
    name: 'dataset.load',
    enabled: true,
    description: 'Load a dataset by ID, optionally selecting a specific slice index.'
  },
  {
    name: 'view.goto',
    enabled: true,
    description: 'Navigate the sphere to an absolute longitude/latitude (with optional zoom).'
  },
  {
    name: 'view.zoom',
    enabled: true,
    description: 'Set the sphere zoom level (absolute).'
  },
  {
    name: 'view.rotation',
    enabled: true,
    description: 'Enable or disable auto-rotation and optionally set the rotation speed.'
  },
  {
    name: 'media.play',
    enabled: true,
    description: 'Start (resume) media playback.'
  },
  {
    name: 'media.pause',
    enabled: true,
    description: 'Pause media playback.'
  },
  {
    name: 'media.stop',
    enabled: true,
    description: 'Stop media playback and reset the playhead.'
  },
  {
    name: 'media.seek',
    enabled: true,
    description: 'Seek media playback to a specific time (seconds).'
  },
  {
    name: 'media.seekDate',
    enabled: true,
    description: 'Go to a date or time in a dataset that has a timeline (ISO 8601, e.g. 2026-09-20 or 2026-09-20T12:00Z).'
  },
  {
    name: 'media.speed',
    enabled: true,
    description: 'Set media playback speed (0.1–10).'
  },
  {
    name: 'sphere.slice',
    enabled: true,
    description: 'Set the number of rendered sphere slices (1–4).'
  },
  {
    name: 'state.describe',
    enabled: true,
    description: 'Return the current semantic observation (dataset, media, canvas, capabilities).'
  },
  {
    name: 'luma.describe',
    enabled: true,
    description:
      'Describe the value-encoded dataset on screen: units, value range, classes, geographic coverage, grid size, and frame time. Returns available:false when the current dataset is not value-encoded.'
  },
  {
    name: 'luma.palette',
    enabled: true,
    description:
      "Set the colour palette of the value-encoded layer: 'default' (the dataset's published palette), 'grayscale', 'turbo', 'viridis', 'magma', 'inferno', 'plasma', 'cividis', 'coolwarm', or any of those with a '-reversed' suffix."
  },
  {
    name: 'luma.stretch',
    enabled: true,
    description:
      'Set the display range of the value-encoded layer in physical units (lo/hi), or reset it to the dataset full range.'
  },
  {
    name: 'luma.probe',
    enabled: true,
    description:
      'Read the physical value at a latitude/longitude on the value-encoded layer, with units (or the class label for classified datasets). Datasets with auxiliary classified regions also report each region\'s class (e.g. precipitation phase: rain or snow) in `auxiliary`, and `text` gives the readout line, e.g. "1.6 mm/hr · snow".'
  },
  {
    name: 'luma.probeMany',
    enabled: true,
    description:
      'Read several latitude/longitude positions from the SAME frame, returning one timestamp for the whole answer. Use this whenever positions are to be compared: separate luma.probe calls can land on different frames of a moving stream, which makes any comparison between them unsupportable.'
  },
  {
    name: 'luma.stats',
    enabled: true,
    description:
      'Area-weighted statistics over a lat/lon box (or the whole frame): min, max, mean, median, p10, p90, standard deviation, coverage and area. Optionally the area above a threshold value and the value histogram.'
  },
  {
    name: 'luma.extremum',
    enabled: true,
    description:
      'Locate the highest or lowest value in a lat/lon box (or the whole frame), reporting whether the extreme is a plateau shared by many texels rather than a single point.'
  },
  {
    name: 'luma.transect',
    enabled: true,
    description:
      'Sample the field along the great circle between two lat/lon points, with a summary of the profile.'
  },
  {
    name: 'luma.zonal',
    enabled: true,
    description:
      'Compute the zonal-mean profile: the mean value against latitude with longitude integrated out.'
  },
  // Measurement pins: AYNI 3D's measurement points, AYNI XR's Data tab.
  {
    name: 'pins.place',
    enabled: true,
    description:
      'Place measurement pins (at most 4) at latitude/longitude points to follow the data there over time; the values fill in as the dataset plays, so start playback to collect them. If the visitor has not said where, ask them where first; never pick places yourself. replace: true clears the existing pins first.'
  },
  {
    name: 'pins.read',
    enabled: true,
    description:
      'Read every measurement pin: where it is, its value now with units, and over the time played so far its lowest, highest, mean and latest value with the date of the latest, plus how much of the dataset\'s time has been read (coverage). Report changes only from these numbers.'
  },
  {
    name: 'pins.clear',
    enabled: true,
    description: 'Remove every measurement pin.'
  },
  {
    name: 'pins.extremes',
    enabled: true,
    description: 'Place two pins on the highest and the lowest value on screen now (replacing earlier highest/lowest pins).'
  },
  // AYNI XR's Research and Education environments
  // (docs/AI_CHATBOT_RESEARCH_EDUCATION_CONTRACT.md). Off here: AYNI 3D and
  // 2D do not carry them out; AYNI XR opts in through its own command list.
  // Each answers status ok (values, method, cautions), pending, unavailable
  // (with the reason) or locked (with the reason).
  {
    name: 'stats.trend',
    enabled: false,
    description:
      'The trend of one measurement pin\'s values over the time read so far: least-squares slope with its 95 % interval and R², and Mann–Kendall with Sen\'s slope. Rising or falling only when the verdict says up or down. Give the method and every caution returned. pin: the id from pins_read.'
  },
  {
    name: 'stats.emergence',
    enabled: false,
    description:
      'Time of emergence at one pin: the year its 10-year running mean leaves the baseline ± 2σ for good. Needs annual or coarser values over 30 years or more. Optional baseline years and a noise level in the data\'s units.'
  },
  {
    name: 'stats.correlation',
    enabled: false,
    description:
      'How two pins\' values vary together: Pearson and Spearman with effective sample size, the same after removing each trend, and the best lag. A correlation never shows cause: always say so.'
  },
  {
    name: 'baseline.hold',
    enabled: false,
    description:
      "Hold a baseline to measure change against: frame 'current' (the frame on screen), or period 'default' (the standard 30-year climate period, 1991–2020 where the data has it) or {from, to} years. A period reads every frame first and answers pending with the frames read so far."
  },
  {
    name: 'baseline.status',
    enabled: false,
    description: 'What baseline is held, and for a period how many of its frames have been read.'
  },
  {
    name: 'baseline.clear',
    enabled: false,
    description: 'Let go of the held baseline.'
  },
  {
    name: 'change.show',
    enabled: false,
    description:
      "Show the globe as change from the held baseline: 'difference' (in the data's units), 'percent', 'sigma' (standardised anomaly; needs a period baseline) or 'off'."
  },
  {
    name: 'compare.run',
    enabled: false,
    description:
      "Compare the dataset with an overlay or another globe over a region: bias, RMS difference, centred RMS difference and pattern correlation. Refused when the units cannot be matched. There is no significance test for whole maps: say so."
  },
  {
    name: 'threshold.area',
    enabled: false,
    description:
      "The area (km² and % of the region) above or below a threshold, on the frame on screen: of the value, of the change from the baseline ('difference'), or of the change in σ ('sigma'). Gives a low–high range for cells near the threshold."
  },
  {
    name: 'hovmoller.summary',
    enabled: false,
    description:
      'Summarise the line-over-time diagram (Hovmöller) along the Analyze Line: rows read, time span, lowest, highest and mean. Give from and to to set the Line first; otherwise the Line the user drew is used.'
  },
  {
    name: 'methods.get',
    enabled: false,
    description: 'The methods paragraph and reference list for what has been done in this session, built from the app\'s own state.'
  },
  {
    name: 'figure.open',
    enabled: false,
    description: "Open the publication figure dialog (kind 'globe' or 'equal-earth'). The user exports it; you never do."
  },
  {
    name: 'bundle.open',
    enabled: false,
    description: 'Show the Bundle button that saves the data, figure and methods as one zip. The user presses it; you never do.'
  },
  {
    name: 'cite.view',
    enabled: false,
    description: "How to cite the dataset on screen ('text', 'bibtex' or 'ris'), with a link that reopens this view."
  },
  {
    name: 'lesson.find',
    enabled: false,
    description: 'Find lessons by words, grade (0 = kindergarten to 12), standard code (e.g. MS-ESS2-5) or unit.'
  },
  {
    name: 'lesson.open',
    enabled: false,
    description: 'Open a lesson by its slug from lesson_find, at its first step. Only when asked.'
  },
  {
    name: 'lesson.context',
    enabled: false,
    description: 'The lesson in progress: its driving question, the current step\'s questions and tasks, and the step\'s dataset.'
  },
  {
    name: 'lesson.hint',
    enabled: false,
    description: 'What to look at for a lesson step (the current one by default): the dataset, the places the task names, the legend and the tool to use. Never the answer.'
  },
  {
    name: 'misconception.check',
    enabled: false,
    description: "Compare a student's idea with the lesson's known misconception and what is actually true. For teachers."
  },
  {
    name: 'explain.value',
    enabled: false,
    description:
      'What a reading on the globe means: units, range, the smallest step the data can show, the palette and what a gap (no data) means here. With lat and lon, the reading there too.'
  },
  // DSA 1.8 vector layers. Off here until AYNI 3D and 2D carry it out; AYNI XR opts in.
  {
    name: 'features.query',
    enabled: false,
    description:
      'Read the records behind the points, lines or areas a dataset draws itself (for example earthquakes with magnitude, depth and place), from the data, not from colours. ' +
      "when 'shown' (default): those drawn for the date on screen; 'all': the whole period, or from/to ISO dates. " +
      'Rank with sortBy (a numeric field, e.g. "mag"; order desc gives the largest first), narrow with where and bounds. ' +
      'Answers the layer\'s fields with units, how many matched, the top features with time, place and values, and min/max/mean per numeric field.'
  },
  // The Satellites layer and the catalog. Off here (no Satellites layer in AYNI 3D and 2D); AYNI XR opts in.
  {
    name: 'satellites.sources',
    enabled: false,
    description:
      'Which satellites the dataset on screen comes from: those of the frame shown (or of a date), which are drawn on the globe and why one is not (retired, a stand-in, an old frame), every satellite of the dataset with its period, and what each one sees (its field of view).'
  },
  {
    name: 'satellites.datasets',
    enabled: false,
    description:
      'The datasets a satellite feeds, by its name ("Sentinel-1", "GOES-19"; a mission name matches each of its satellites) or NORAD id: each dataset with its id for dataset_load, its period and whether the plan includes it.'
  },
  {
    name: 'satellites.show',
    enabled: false,
    description:
      'Show the Satellites layer on the globe (on false hides it), select a satellite by NORAD id so it is marked with its card, and switch its field of view on or off.'
  },
  {
    name: 'satellites.passes',
    enabled: false,
    description:
      "The next passes over the visitor of satellites that feed the catalog's datasets, with those datasets. Only in AR, once Sky above me is on."
  },
  {
    name: 'satellites.events',
    enabled: false,
    description:
      'Space events (launches, re-entries, notable ones) with the datasets their satellites feed; open (an event id) shows one on the globe.'
  },
  // AYNI XR's Solar System mode. Off here (no Solar System mode in AYNI 3D and 2D); AYNI XR opts in.
  {
    name: 'solar.open',
    enabled: false,
    description:
      'Show the Solar System: the Sun, the planets, the Moon, Ceres and Pluto where they are on the clock\'s date (today unless changed). From a body, back to the whole system.'
  },
  {
    name: 'solar.visit',
    enabled: false,
    description:
      'Go to a body of the Solar System: its globe with its first dataset, and Browse keeps to that body\'s datasets. From another body it flies or fades there, as the visitor chose.'
  },
  {
    name: 'solar.view',
    enabled: false,
    description:
      'The Solar System\'s layout and clock: layout distances (true distances, bodies as dots) or sizes (true sizes, distances squeezed); play true moves the planets, false stops them; speed is how much time passes each second (day, week, month or year); today true goes back to today. Give only what changes.'
  },
  {
    name: 'solar.close',
    enabled: false,
    description: 'Leave the Solar System and go back to the globe and the dataset shown before.'
  },
  // Khipus: guided stories of steps on the sphere. Off here; an app that
  // plays them lists them in its orchestrator's extraCommands (AYNI 3D).
  {
    name: 'khipu.list',
    enabled: false,
    description: 'The Khipus (guided stories: a series of steps, each a dataset, place and words) in this library, matching an optional query in English or the visitor\'s words.'
  },
  {
    name: 'khipu.open',
    enabled: false,
    description: 'Open a Khipu by an id from khipu_list and play it, from its first step or from step (1-based). Only when asked to start a story or a guided sequence.'
  },
  {
    name: 'khipu.step',
    enabled: false,
    description: 'Move the open Khipu: to "next", "previous", or a step number (1-based).'
  },
  {
    name: 'khipu.close',
    enabled: false,
    description: 'Close the open Khipu; the sphere goes back to how it was before it opened.'
  }
];

/** Most Khipus khipu.list answers with. */
export const MAX_AGENT_KHIPU_RESULTS = 10;

export function getAgentCapabilities(): AgentCapability[] {
  return AGENT_CAPABILITIES.map((capability) => ({ ...capability }));
}

/** Commands main answers itself (the catalog, the Khipu library): never sent to the renderer. */
const ANSWERED_IN_MAIN: ReadonlySet<string> = new Set(['catalog.search', 'khipu.list']);

export function isAgentControlCommandType(
  type: string
): type is Exclude<AgentCommand['type'], 'catalog.search' | 'khipu.list'> {
  return (
    !ANSWERED_IN_MAIN.has(type) &&
    AGENT_CAPABILITIES.some((capability) => capability.name === type)
  );
}

export interface AgentCommandValidationFailure {
  ok: false;
  error: { code: string; message: string; details?: unknown };
}

export interface AgentCommandValidationSuccess {
  ok: true;
  command: AgentCommand;
}

export type AgentCommandValidation = AgentCommandValidationSuccess | AgentCommandValidationFailure;

function fail(code: string, message: string, details?: unknown): AgentCommandValidationFailure {
  return { ok: false, error: { code, message, details } };
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Geographic coordinates for the analysis commands.
 *
 * Latitude is bounded by the poles rather than by the sphere view's
 * MIN/MAX_VIEW_LATITUDE_DEG: those clamp where the *camera* may point, while
 * an analysis box may legitimately cover ground the camera cannot centre on.
 */
function validateLatLon(
  lat: unknown,
  lon: unknown,
  label: string
): AgentCommandValidationFailure | null {
  if (!isFiniteNumber(lat) || lat < -90 || lat > 90) {
    return fail('INVALID_AGENT_COMMAND', `${label} latitude must be in [-90, 90].`);
  }
  if (!isFiniteNumber(lon) || lon < -180 || lon > 180) {
    return fail('INVALID_AGENT_COMMAND', `${label} longitude must be in [-180, 180].`);
  }
  return null;
}

function validateOptionalBounds(
  bounds: unknown,
  label: string
): AgentCommandValidationFailure | null {
  if (bounds === undefined || bounds === null) return null;
  if (typeof bounds !== 'object' || Array.isArray(bounds)) {
    return fail('INVALID_AGENT_COMMAND', `${label} bounds must be an object with n, s, w, e.`);
  }
  const { n, s, w, e } = bounds as Record<string, unknown>;
  if (!isFiniteNumber(n) || !isFiniteNumber(s) || n < -90 || n > 90 || s < -90 || s > 90) {
    return fail('INVALID_AGENT_COMMAND', `${label} bounds n and s must be latitudes in [-90, 90].`);
  }
  if (n <= s) {
    return fail('INVALID_AGENT_COMMAND', `${label} bounds require n > s.`);
  }
  if (!isFiniteNumber(w) || !isFiniteNumber(e) || w < -180 || w > 180 || e < -180 || e > 180) {
    return fail('INVALID_AGENT_COMMAND', `${label} bounds w and e must be longitudes in [-180, 180].`);
  }
  // w > e is legal: it means the box crosses the antimeridian.
  return null;
}

/** Longest catalog query an agent may send; longer text is not a search. */
export const MAX_AGENT_CATALOG_QUERY_LENGTH = 200;
/** Most catalog results an agent may ask for (the search service's own cap). */
export const MAX_AGENT_CATALOG_LIMIT = 50;
/** Most measurement pins one command places (AYNI XR's Data tab holds four). */
export const MAX_AGENT_PINS = 4;
/** Most pin ids one clear names (AYNI 3D holds at most 24 measurements). */
const MAX_AGENT_PIN_IDS = 24;

/** Longest pin id, lesson slug, overlay id, standard or unit an agent may name. */
const MAX_AGENT_ID_LENGTH = 100;
/** Longest student idea `misconception.check` takes. */
export const MAX_AGENT_CLAIM_LENGTH = 500;
/** Most features one `features.query` lists (the engine cuts a tool result at 3,000 characters). */
export const MAX_AGENT_FEATURES = 10;
/** Most field conditions one `features.query` takes. */
export const MAX_AGENT_FEATURE_FILTERS = 4;
/** Most space events one `satellites.events` lists. */
export const MAX_AGENT_SATELLITE_EVENTS = 10;
/** XR's lesson grades: 0 (kindergarten) to 13. */
const MAX_AGENT_LESSON_GRADE = 13;
/** Most lessons one `lesson.find` returns. */
const MAX_AGENT_LESSON_RESULTS = 10;

function isShortId(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= MAX_AGENT_ID_LENGTH;
}

function requireId(value: unknown, label: string): AgentCommandValidationFailure | null {
  return isShortId(value) ? null : fail('INVALID_AGENT_COMMAND', `${label} must be a non-empty id of at most ${MAX_AGENT_ID_LENGTH} characters.`);
}

function optionalId(value: unknown, label: string): AgentCommandValidationFailure | null {
  return value === undefined ? null : requireId(value, label);
}

function optionalEnum(value: unknown, allowed: readonly string[], label: string): AgentCommandValidationFailure | null {
  if (value === undefined || allowed.includes(value as string)) return null;
  return fail('INVALID_AGENT_COMMAND', `${label} must be one of ${allowed.map((entry) => `'${entry}'`).join(', ')}.`);
}

function requireEnum(value: unknown, allowed: readonly string[], label: string): AgentCommandValidationFailure | null {
  return value === undefined ? fail('INVALID_AGENT_COMMAND', `${label} is required.`) : optionalEnum(value, allowed, label);
}

/** Whole years, `from` before `to`; the module checks the span against the data. */
function validateYearSpan(span: unknown, label: string): AgentCommandValidationFailure | null {
  if (typeof span !== 'object' || span === null || Array.isArray(span)) {
    return fail('INVALID_AGENT_COMMAND', `${label} must be an object with from and to years.`);
  }
  const { from, to } = span as Record<string, unknown>;
  if (!Number.isInteger(from) || !Number.isInteger(to) || (from as number) < 1 || (to as number) > 9999) {
    return fail('INVALID_AGENT_COMMAND', `${label} from and to must be whole years.`);
  }
  if ((from as number) >= (to as number)) {
    return fail('INVALID_AGENT_COMMAND', `${label} requires from < to.`);
  }
  return null;
}

/** Both of two optional points, or neither. */
function validateOptionalPointPair(
  from: unknown,
  to: unknown,
  label: string
): AgentCommandValidationFailure | null {
  if (from === undefined && to === undefined) return null;
  if (from === undefined || to === undefined) {
    return fail('INVALID_AGENT_COMMAND', `${label} needs both from and to, or neither.`);
  }
  const a = from as { lat?: unknown; lon?: unknown } | null;
  const b = to as { lat?: unknown; lon?: unknown } | null;
  return validateLatLon(a?.lat, a?.lon, `${label} from`) ?? validateLatLon(b?.lat, b?.lon, `${label} to`);
}

function ok(command: Record<string, unknown>): AgentCommandValidationSuccess {
  return { ok: true, command: command as unknown as AgentCommand };
}

/** An ISO 8601 date or date-time a model may send. */
function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && value.length <= 40 && /^\d{4}-\d{2}-\d{2}/.test(value) && Number.isFinite(Date.parse(value));
}

/** `features.query`: every argument optional; only the known ones are kept. */
function validateFeaturesQuery(command: Record<string, unknown>): AgentCommandValidation {
  const label = 'features.query';
  const error =
    optionalId(command.layer, `${label} layer`) ??
    optionalEnum(command.when, ['shown', 'all'], `${label} when`) ??
    validateOptionalBounds(command.bounds, label) ??
    optionalId(command.sortBy, `${label} sortBy`) ??
    optionalEnum(command.order, ['desc', 'asc'], `${label} order`);
  if (error) return error;
  for (const key of ['from', 'to'] as const) {
    if (command[key] !== undefined && !isIsoDate(command[key])) {
      return fail('INVALID_AGENT_COMMAND', `${label} ${key} must be an ISO date, e.g. "2026-09-20".`);
    }
  }
  const limit = command.limit;
  if (limit !== undefined && !(Number.isInteger(limit) && (limit as number) >= 1 && (limit as number) <= MAX_AGENT_FEATURES)) {
    return fail('INVALID_AGENT_COMMAND', `${label} limit must be an integer in [1, ${MAX_AGENT_FEATURES}].`);
  }
  const where = command.where;
  if (where !== undefined && !(Array.isArray(where) && where.length <= MAX_AGENT_FEATURE_FILTERS)) {
    return fail('INVALID_AGENT_COMMAND', `${label} where must be a list of at most ${MAX_AGENT_FEATURE_FILTERS} conditions.`);
  }
  const filters: Record<string, unknown>[] = [];
  for (const [index, entry] of ((where as unknown[] | undefined) ?? []).entries()) {
    const at = `${label} where ${index}`;
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
      return fail('INVALID_AGENT_COMMAND', `${at} must be an object with field and min, max or equals.`);
    }
    const { field, min, max, equals } = entry as Record<string, unknown>;
    const fieldError = requireId(field, `${at} field`);
    if (fieldError) return fieldError;
    if ((min !== undefined && !isFiniteNumber(min)) || (max !== undefined && !isFiniteNumber(max))) {
      return fail('INVALID_AGENT_COMMAND', `${at} min and max must be finite numbers.`);
    }
    const equalsOk =
      equals === undefined ||
      typeof equals === 'boolean' ||
      isFiniteNumber(equals) ||
      (typeof equals === 'string' && equals.length <= MAX_AGENT_ID_LENGTH);
    if (!equalsOk) return fail('INVALID_AGENT_COMMAND', `${at} equals must be text, a number or true/false.`);
    if (min === undefined && max === undefined && equals === undefined) {
      return fail('INVALID_AGENT_COMMAND', `${at} needs min, max or equals.`);
    }
    filters.push({ field, ...(min !== undefined ? { min } : {}), ...(max !== undefined ? { max } : {}), ...(equals !== undefined ? { equals } : {}) });
  }
  const clean: Record<string, unknown> = { type: 'features.query' };
  for (const key of ['layer', 'when', 'from', 'to', 'bounds', 'sortBy', 'order', 'limit'] as const) {
    if (command[key] !== undefined && command[key] !== null) clean[key] = command[key];
  }
  if (filters.length) clean.where = filters;
  return ok(clean);
}

/** The research, general and education commands (contract §4); null for any other type. */
function validateResearchEducationCommand(type: string, command: Record<string, unknown>): AgentCommandValidation | null {
  switch (type) {
    case 'stats.trend':
      return requireId(command.pin, 'stats.trend pin') ?? ok({ type, pin: command.pin });
    case 'stats.emergence': {
      const error =
        requireId(command.pin, 'stats.emergence pin') ??
        (command.baseline === undefined ? null : validateYearSpan(command.baseline, 'stats.emergence baseline'));
      if (error) return error;
      if (command.noise !== undefined && !(isFiniteNumber(command.noise) && command.noise > 0)) {
        return fail('INVALID_AGENT_COMMAND', 'stats.emergence noise must be a positive number in the data\'s units.');
      }
      return ok(command);
    }
    case 'stats.correlation': {
      const error = requireId(command.pinA, 'stats.correlation pinA') ?? requireId(command.pinB, 'stats.correlation pinB');
      if (error) return error;
      if (command.pinA === command.pinB) {
        return fail('INVALID_AGENT_COMMAND', 'stats.correlation needs two different pins.');
      }
      return ok({ type, pinA: command.pinA, pinB: command.pinB });
    }
    case 'baseline.hold': {
      const { frame, period } = command;
      if ((frame === undefined) === (period === undefined)) {
        return fail('INVALID_AGENT_COMMAND', "baseline.hold takes exactly one of frame: 'current' or period.");
      }
      if (frame !== undefined) {
        return frame === 'current'
          ? ok({ type, frame })
          : fail('INVALID_AGENT_COMMAND', "baseline.hold frame must be 'current'.");
      }
      // The model's schema offers {} for the default period (no anyOf).
      const isEmptyObject = typeof period === 'object' && period !== null && !Array.isArray(period) && Object.keys(period).length === 0;
      if (period === 'default' || isEmptyObject) return ok({ type, period: 'default' });
      return validateYearSpan(period, 'baseline.hold period') ?? ok({ type, period });
    }
    case 'baseline.status':
    case 'baseline.clear':
    case 'methods.get':
    case 'bundle.open':
    case 'lesson.context':
      return ok({ type });
    case 'change.show':
      return requireEnum(command.mode, ['off', 'difference', 'percent', 'sigma'], 'change.show mode') ?? ok({ type, mode: command.mode });
    case 'compare.run': {
      const error =
        requireEnum(command.source, ['overlay', 'globe'], 'compare.run source') ??
        optionalId(command.id, 'compare.run id') ??
        optionalEnum(command.pairing, ['time', 'shown'], 'compare.run pairing') ??
        optionalEnum(command.mode, ['difference', 'percent'], 'compare.run mode') ??
        validateOptionalBounds(command.bounds, 'compare.run');
      return error ?? ok(command);
    }
    case 'threshold.area': {
      const error =
        requireEnum(command.mode, ['value', 'difference', 'sigma'], 'threshold.area mode') ??
        requireEnum(command.comparator, ['above', 'below'], 'threshold.area comparator') ??
        validateOptionalBounds(command.bounds, 'threshold.area');
      if (error) return error;
      if (!isFiniteNumber(command.threshold)) {
        return fail('INVALID_AGENT_COMMAND', 'threshold.area threshold must be a finite number (σ for sigma).');
      }
      return ok(command);
    }
    case 'hovmoller.summary':
      return validateOptionalPointPair(command.from, command.to, 'hovmoller.summary') ?? ok(command);
    case 'figure.open':
      return optionalEnum(command.kind, ['globe', 'equal-earth'], 'figure.open kind') ?? ok(command);
    case 'cite.view':
      return optionalEnum(command.format, ['text', 'bibtex', 'ris'], 'cite.view format') ?? ok(command);
    case 'lesson.find': {
      const { query, grade, limit } = command;
      if (query !== undefined && !(typeof query === 'string' && query.trim().length > 0 && query.length <= MAX_AGENT_CATALOG_QUERY_LENGTH)) {
        return fail('INVALID_AGENT_COMMAND', `lesson.find query must be non-empty text of at most ${MAX_AGENT_CATALOG_QUERY_LENGTH} characters.`);
      }
      if (grade !== undefined && !(Number.isInteger(grade) && (grade as number) >= 0 && (grade as number) <= MAX_AGENT_LESSON_GRADE)) {
        return fail('INVALID_AGENT_COMMAND', `lesson.find grade must be a whole number from 0 (kindergarten) to ${MAX_AGENT_LESSON_GRADE}.`);
      }
      if (limit !== undefined && !(Number.isInteger(limit) && (limit as number) >= 1 && (limit as number) <= MAX_AGENT_LESSON_RESULTS)) {
        return fail('INVALID_AGENT_COMMAND', `lesson.find limit must be an integer in [1, ${MAX_AGENT_LESSON_RESULTS}].`);
      }
      return optionalId(command.standard, 'lesson.find standard') ?? optionalId(command.unit, 'lesson.find unit') ?? ok(command);
    }
    case 'lesson.open':
      return requireId(command.slug, 'lesson.open slug') ?? ok({ type, slug: command.slug });
    case 'lesson.hint': {
      const step = command.step;
      if (step !== undefined && !(Number.isInteger(step) && (step as number) >= 1)) {
        return fail('INVALID_AGENT_COMMAND', 'lesson.hint step must be a step number from 1.');
      }
      return ok(step === undefined ? { type } : { type, step });
    }
    case 'misconception.check': {
      const claim = command.claim;
      if (typeof claim !== 'string' || claim.trim().length === 0 || claim.length > MAX_AGENT_CLAIM_LENGTH) {
        return fail('INVALID_AGENT_COMMAND', `misconception.check claim must be non-empty text of at most ${MAX_AGENT_CLAIM_LENGTH} characters.`);
      }
      return ok({ type, claim });
    }
    case 'explain.value': {
      const { lat, lon } = command;
      if (lat === undefined && lon === undefined) return ok({ type });
      return validateLatLon(lat, lon, 'explain.value') ?? ok({ type, lat, lon });
    }
    case 'features.query':
      return validateFeaturesQuery(command);
    case 'satellites.sources': {
      const date = command.date;
      if (date === undefined || date === null) return ok({ type });
      return isIsoDate(date) ? ok({ type, date }) : fail('INVALID_AGENT_COMMAND', 'satellites.sources date must be an ISO date, e.g. "1985-06-01".');
    }
    case 'satellites.datasets': {
      // A model may send a NORAD id as a number; the executor matches text.
      const raw = command.satellite;
      const satellite = Number.isInteger(raw) && (raw as number) > 0 ? String(raw) : raw;
      return requireId(satellite, 'satellites.datasets satellite') ?? ok({ type, satellite: (satellite as string).trim() });
    }
    case 'satellites.show': {
      const { on, norad, fov } = command;
      if ((on !== undefined && typeof on !== 'boolean') || (fov !== undefined && typeof fov !== 'boolean')) {
        return fail('INVALID_AGENT_COMMAND', 'satellites.show on and fov must be true or false.');
      }
      if (norad !== undefined && !(Number.isInteger(norad) && (norad as number) > 0)) {
        return fail('INVALID_AGENT_COMMAND', 'satellites.show norad must be a NORAD catalogue number.');
      }
      if (on === false && norad !== undefined) return fail('INVALID_AGENT_COMMAND', 'satellites.show cannot select a satellite while hiding the layer.');
      const clean: Record<string, unknown> = { type };
      if (on !== undefined) clean.on = on;
      if (norad !== undefined) clean.norad = norad;
      if (fov !== undefined) clean.fov = fov;
      return ok(clean);
    }
    case 'satellites.passes':
      return ok({ type });
    case 'solar.open':
    case 'solar.close':
      return ok({ type });
    case 'solar.visit': {
      const body = typeof command.body === 'string' ? command.body.trim().toLowerCase() : command.body;
      return (AGENT_SOLAR_BODIES as readonly unknown[]).includes(body)
        ? ok({ type, body })
        : fail('INVALID_AGENT_COMMAND', `solar.visit body must be one of ${AGENT_SOLAR_BODIES.join(', ')}.`);
    }
    case 'solar.view': {
      const { layout, play, speed, today } = command;
      if (layout !== undefined && layout !== 'distances' && layout !== 'sizes') return fail('INVALID_AGENT_COMMAND', 'solar.view layout must be distances or sizes.');
      if ((play !== undefined && typeof play !== 'boolean') || (today !== undefined && typeof today !== 'boolean')) {
        return fail('INVALID_AGENT_COMMAND', 'solar.view play and today must be true or false.');
      }
      if (speed !== undefined && !(AGENT_SOLAR_SPEEDS as readonly unknown[]).includes(speed)) {
        return fail('INVALID_AGENT_COMMAND', `solar.view speed must be one of ${AGENT_SOLAR_SPEEDS.join(', ')}.`);
      }
      if (layout === undefined && play === undefined && speed === undefined && today === undefined) {
        return fail('INVALID_AGENT_COMMAND', 'solar.view needs a layout, play, speed or today.');
      }
      return ok({ type, ...(layout !== undefined ? { layout } : {}), ...(play !== undefined ? { play } : {}), ...(speed !== undefined ? { speed } : {}), ...(today !== undefined ? { today } : {}) });
    }
    case 'satellites.events': {
      const { open, limit } = command;
      if (limit !== undefined && !(Number.isInteger(limit) && (limit as number) >= 1 && (limit as number) <= MAX_AGENT_SATELLITE_EVENTS)) {
        return fail('INVALID_AGENT_COMMAND', `satellites.events limit must be an integer in [1, ${MAX_AGENT_SATELLITE_EVENTS}].`);
      }
      const error = optionalId(open, 'satellites.events open');
      if (error) return error;
      return ok({ type, ...(open !== undefined ? { open } : {}), ...(limit !== undefined ? { limit } : {}) });
    }
    case 'khipu.list': {
      const { query, limit } = command;
      if (query !== undefined && (typeof query !== 'string' || query.length > MAX_AGENT_CATALOG_QUERY_LENGTH)) {
        return fail('INVALID_AGENT_COMMAND', `khipu.list query must be text of at most ${MAX_AGENT_CATALOG_QUERY_LENGTH} characters.`);
      }
      if (limit !== undefined && !(Number.isInteger(limit) && (limit as number) >= 1 && (limit as number) <= MAX_AGENT_KHIPU_RESULTS)) {
        return fail('INVALID_AGENT_COMMAND', `khipu.list limit must be an integer in [1, ${MAX_AGENT_KHIPU_RESULTS}].`);
      }
      return ok({ type, ...(typeof query === 'string' && query.trim() ? { query: query.trim() } : {}), ...(limit !== undefined ? { limit } : {}) });
    }
    case 'khipu.open': {
      const error = requireId(command.khipuId, 'khipu.open khipuId');
      if (error) return error;
      const step = command.step;
      if (step !== undefined && !(Number.isInteger(step) && (step as number) >= 1)) {
        return fail('INVALID_AGENT_COMMAND', 'khipu.open step must be a whole number from 1.');
      }
      return ok({ type, khipuId: command.khipuId, ...(step !== undefined ? { step } : {}) });
    }
    case 'khipu.step': {
      const to = command.to;
      if (to !== 'next' && to !== 'previous' && !(Number.isInteger(to) && (to as number) >= 1)) {
        return fail('INVALID_AGENT_COMMAND', 'khipu.step to must be "next", "previous" or a step number from 1.');
      }
      return ok({ type, to });
    }
    case 'khipu.close':
      return ok({ type });
    default:
      return null;
  }
}

function validateOptionalSliceIndex(value: unknown, label: string): AgentCommandValidationFailure | null {
  if (value === undefined) return null;
  return Number.isInteger(value) && (value as number) >= 0
    ? null
    : fail('INVALID_AGENT_COMMAND', `${label} sliceIndex must be a non-negative integer.`);
}

/**
 * Validates the shape + range of an `AgentCommand` payload: every advertised
 * capability, `catalog.search` included. Catalog search is answered in main
 * (see isAgentControlCommandType); the rest are control commands the renderer
 * executes.
 */
export function validateAgentCommand(raw: unknown): AgentCommandValidation {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return fail('INVALID_AGENT_COMMAND', 'Command must be an object.');
  }
  const command = raw as Record<string, unknown>;
  const type = command.type;

  switch (type) {
    case 'catalog.search': {
      const query = command.query;
      if (typeof query !== 'string' || query.trim().length === 0) {
        return fail('INVALID_AGENT_COMMAND', 'catalog.search requires a non-empty query string.');
      }
      if (query.length > MAX_AGENT_CATALOG_QUERY_LENGTH) {
        return fail('INVALID_AGENT_COMMAND', `catalog.search query must be at most ${MAX_AGENT_CATALOG_QUERY_LENGTH} characters.`);
      }
      const categories = command.categories;
      if (categories !== undefined && !(Array.isArray(categories) && categories.every((entry) => typeof entry === 'string'))) {
        return fail('INVALID_AGENT_COMMAND', 'catalog.search categories must be an array of strings.');
      }
      const limit = command.limit;
      if (limit !== undefined && !(Number.isInteger(limit) && (limit as number) >= 1 && (limit as number) <= MAX_AGENT_CATALOG_LIMIT)) {
        return fail('INVALID_AGENT_COMMAND', `catalog.search limit must be an integer in [1, ${MAX_AGENT_CATALOG_LIMIT}].`);
      }
      return { ok: true, command: command as unknown as AgentCommand };
    }
    case 'dataset.load': {
      const datasetId = command.datasetId;
      if (typeof datasetId !== 'string' || datasetId.length === 0) {
        return fail('INVALID_AGENT_COMMAND', 'dataset.load requires a non-empty datasetId string.');
      }
      const sliceId = command.sliceId;
      if (sliceId !== undefined && !(Number.isInteger(sliceId) && (sliceId as number) >= 0)) {
        return fail('INVALID_AGENT_COMMAND', 'dataset.load sliceId must be a non-negative integer.');
      }
      return { ok: true, command: command as unknown as AgentCommand };
    }
    case 'view.goto': {
      const { longitude, latitude } = command;
      if (!isFiniteNumber(longitude) || longitude < -180 || longitude > 180) {
        return fail('INVALID_AGENT_COMMAND', 'view.goto longitude must be in [-180, 180].');
      }
      if (
        !isFiniteNumber(latitude) ||
        latitude < MIN_VIEW_LATITUDE_DEG ||
        latitude > MAX_VIEW_LATITUDE_DEG
      ) {
        return fail(
          'INVALID_AGENT_COMMAND',
          `view.goto latitude must be in [${MIN_VIEW_LATITUDE_DEG}, ${MAX_VIEW_LATITUDE_DEG}].`
        );
      }
      const zoom = command.zoom;
      if (zoom !== undefined && !(isFiniteNumber(zoom) && zoom > 0 && zoom <= AGENT_MAX_ZOOM)) {
        return fail('INVALID_AGENT_COMMAND', `view.goto zoom must be a positive number up to ${AGENT_MAX_ZOOM}.`);
      }
      return { ok: true, command: command as unknown as AgentCommand };
    }
    case 'view.zoom': {
      if (!isFiniteNumber(command.zoom) || (command.zoom as number) <= 0 || (command.zoom as number) > AGENT_MAX_ZOOM) {
        return fail('INVALID_AGENT_COMMAND', `view.zoom requires a positive zoom number up to ${AGENT_MAX_ZOOM}.`);
      }
      return { ok: true, command: command as unknown as AgentCommand };
    }
    case 'view.rotation': {
      if (typeof command.enabled !== 'boolean') {
        return fail('INVALID_AGENT_COMMAND', 'view.rotation requires an enabled boolean.');
      }
      const speed = command.speed;
      if (speed !== undefined && !(isFiniteNumber(speed) && (speed as number) >= 0 && (speed as number) <= AGENT_MAX_ROTATION_SPEED)) {
        return fail('INVALID_AGENT_COMMAND', `view.rotation speed must be between 0 and ${AGENT_MAX_ROTATION_SPEED}.`);
      }
      return { ok: true, command: command as unknown as AgentCommand };
    }
    case 'media.play':
    case 'media.pause':
    case 'media.stop':
      return { ok: true, command: { type } as AgentCommand };
    case 'media.seek': {
      if (!isFiniteNumber(command.time) || (command.time as number) < 0) {
        return fail('INVALID_AGENT_COMMAND', 'media.seek requires a non-negative time (seconds).');
      }
      return { ok: true, command: command as unknown as AgentCommand };
    }
    case 'media.seekDate': {
      const date = command.date;
      if (typeof date !== 'string' || date.length > 40 || !Number.isFinite(Date.parse(date))) {
        return fail('INVALID_AGENT_COMMAND', 'media.seekDate requires an ISO 8601 date, e.g. 2026-09-20.');
      }
      return { ok: true, command: { type, date } as AgentCommand };
    }
    case 'media.speed': {
      const speed = command.speed;
      if (!isFiniteNumber(speed) || (speed as number) <= 0 || (speed as number) > 10) {
        return fail('INVALID_AGENT_COMMAND', 'media.speed requires a number in (0, 10].');
      }
      return { ok: true, command: command as unknown as AgentCommand };
    }
    case 'sphere.slice': {
      const count = command.count;
      if (![1, 2, 3, 4].includes(count as number)) {
        return fail('INVALID_AGENT_COMMAND', 'sphere.slice count must be one of 1, 2, 3, 4.');
      }
      return { ok: true, command: { type, count: count as 1 | 2 | 3 | 4 } as AgentCommand };
    }
    case 'state.describe':
      return { ok: true, command: { type: 'state.describe' } as AgentCommand };
    case 'luma.describe':
      return { ok: true, command: { type: 'luma.describe' } as AgentCommand };
    case 'luma.palette': {
      const palette = command.palette;
      if (typeof palette !== 'string' || palette.length === 0) {
        return fail('INVALID_AGENT_COMMAND', 'luma.palette requires a non-empty palette id string.');
      }
      return { ok: true, command: command as unknown as AgentCommand };
    }
    case 'luma.stretch': {
      const { lo, hi, reset } = command;
      if (reset === true) {
        return { ok: true, command: { type: 'luma.stretch', reset: true } as AgentCommand };
      }
      if (!isFiniteNumber(lo) || !isFiniteNumber(hi)) {
        return fail('INVALID_AGENT_COMMAND', 'luma.stretch requires finite lo and hi values, or reset: true.');
      }
      if ((hi as number) <= (lo as number)) {
        return fail('INVALID_AGENT_COMMAND', 'luma.stretch requires hi > lo.');
      }
      return { ok: true, command: command as unknown as AgentCommand };
    }
    case 'luma.probe': {
      const coordinateError = validateLatLon(command.lat, command.lon, 'luma.probe');
      if (coordinateError) return coordinateError;
      return { ok: true, command: command as unknown as AgentCommand };
    }
    case 'luma.probeMany': {
      const points = command.points;
      if (!Array.isArray(points) || points.length === 0) {
        return fail('INVALID_AGENT_COMMAND', 'luma.probeMany points must be a non-empty array.');
      }
      if (points.length > 64) {
        return fail('INVALID_AGENT_COMMAND', 'luma.probeMany accepts at most 64 points.');
      }
      for (const point of points as Array<{ lat?: unknown; lon?: unknown }>) {
        const coordinateError = validateLatLon(point?.lat, point?.lon, 'luma.probeMany');
        if (coordinateError) return coordinateError;
      }
      return { ok: true, command: command as unknown as AgentCommand };
    }
    case 'luma.stats': {
      const boundsError = validateOptionalBounds(command.bounds, 'luma.stats');
      if (boundsError) return boundsError;
      const threshold = command.threshold;
      if (threshold !== undefined && !isFiniteNumber(threshold)) {
        return fail('INVALID_AGENT_COMMAND', 'luma.stats threshold must be a finite number.');
      }
      return { ok: true, command: command as unknown as AgentCommand };
    }
    case 'luma.extremum': {
      const boundsError = validateOptionalBounds(command.bounds, 'luma.extremum');
      if (boundsError) return boundsError;
      const kind = command.kind;
      if (kind !== undefined && kind !== 'max' && kind !== 'min') {
        return fail('INVALID_AGENT_COMMAND', "luma.extremum kind must be 'max' or 'min'.");
      }
      return { ok: true, command: command as unknown as AgentCommand };
    }
    case 'luma.transect': {
      const from = command.from as { lat?: unknown; lon?: unknown } | undefined;
      const to = command.to as { lat?: unknown; lon?: unknown } | undefined;
      if (!from || !to) {
        return fail('INVALID_AGENT_COMMAND', 'luma.transect requires from and to points.');
      }
      const fromError = validateLatLon(from.lat, from.lon, 'luma.transect from');
      if (fromError) return fromError;
      const toError = validateLatLon(to.lat, to.lon, 'luma.transect to');
      if (toError) return toError;
      const samples = command.samples;
      if (samples !== undefined && !(Number.isInteger(samples) && (samples as number) >= 2)) {
        return fail('INVALID_AGENT_COMMAND', 'luma.transect samples must be an integer >= 2.');
      }
      return { ok: true, command: command as unknown as AgentCommand };
    }
    case 'luma.zonal': {
      const boundsError = validateOptionalBounds(command.bounds, 'luma.zonal');
      if (boundsError) return boundsError;
      const maxRows = command.maxRows;
      if (maxRows !== undefined && !(Number.isInteger(maxRows) && (maxRows as number) >= 2)) {
        return fail('INVALID_AGENT_COMMAND', 'luma.zonal maxRows must be an integer >= 2.');
      }
      return { ok: true, command: command as unknown as AgentCommand };
    }
    case 'pins.place': {
      const points = command.points;
      if (!Array.isArray(points) || points.length < 1 || points.length > MAX_AGENT_PINS) {
        return fail('INVALID_AGENT_COMMAND', `pins.place points must be an array of 1 to ${MAX_AGENT_PINS} positions.`);
      }
      for (const [index, entry] of points.entries()) {
        if (typeof entry !== 'object' || entry === null) {
          return fail('INVALID_AGENT_COMMAND', `pins.place point ${index} must be an object with lat and lon.`);
        }
        const pointError = validateLatLon((entry as Record<string, unknown>).lat, (entry as Record<string, unknown>).lon, `pins.place point ${index}`);
        if (pointError) return pointError;
      }
      if (command.replace !== undefined && typeof command.replace !== 'boolean') {
        return fail('INVALID_AGENT_COMMAND', 'pins.place replace must be a boolean.');
      }
      const sliceError = validateOptionalSliceIndex(command.sliceIndex, 'pins.place');
      if (sliceError) return sliceError;
      return { ok: true, command: command as unknown as AgentCommand };
    }
    case 'pins.read':
    case 'pins.extremes': {
      const sliceError = validateOptionalSliceIndex(command.sliceIndex, type);
      if (sliceError) return sliceError;
      return { ok: true, command: (command.sliceIndex === undefined ? { type } : { type, sliceIndex: command.sliceIndex }) as AgentCommand };
    }
    case 'pins.clear': {
      const ids = command.ids;
      if (ids === undefined) return { ok: true, command: { type } as AgentCommand };
      if (!Array.isArray(ids) || ids.length > MAX_AGENT_PIN_IDS || !ids.every((id) => typeof id === 'string' && id.length > 0 && id.length <= 100)) {
        return fail('INVALID_AGENT_COMMAND', `pins.clear ids must be up to ${MAX_AGENT_PIN_IDS} measurement ids.`);
      }
      return { ok: true, command: { type, ids } as AgentCommand };
    }
    default:
      return (typeof type === 'string' ? validateResearchEducationCommand(type, command) : null) ??
        fail('UNKNOWN_AGENT_COMMAND', `Unknown agent command type: ${String(type)}`);
  }
}
